import type { InspirationItem, MaterialRef, Outline, ProjectDetail, Script, StoredOutline, StoredScript, Topic } from '../../types/api'

export type Tab = 'projects' | 'library'
export type Kind = 'topic' | 'outline' | 'script'
export type StoredCreation = StoredOutline | StoredScript
export type PickerMode = 'builder' | 'project'
export type TopicForm = { title: string; summary: string; angle: string; fit: string }

export type PickerState = { mode: PickerMode; kind?: Kind } | null

export type PreviewState = {
  item: InspirationItem
  edit?: () => void | Promise<void>
}

export const EMPTY_TOPIC: TopicForm = { title: '', summary: '', angle: '', fit: '' }
export const emptyOutline = (_project?: ProjectDetail): Outline => ({
  title: '',
  hook: '',
  tags: '',
  core_content: '',
  outline_text: '',
  reference_topic: null,
})
export const emptyScript = (_project?: ProjectDetail): Script => ({
  title: '',
  script: '',
  reference_outline: null,
})
export const outlineContext = (value: Partial<Outline>) => ({
  title: value.title || null,
  hook: value.hook || null,
  tags: value.tags || null,
  outline_text: value.outline_text || null,
  core_content: value.core_content || null,
})
/** 规范运行时产物只有在包含内容时才有用。 */
export function hasMeaningfulCreation(value: unknown): boolean {
  if (typeof value === 'string') return Boolean(value.trim())
  if (typeof value === 'number' || typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.some(hasMeaningfulCreation)
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    if (record.kind === 'outline') return ['hook', 'tags', 'outline_text', 'core_content'].some(key => hasMeaningfulCreation(record[key]))
    if (record.kind === 'script') return hasMeaningfulCreation(record.script)
    return Object.entries(record).some(([key, item]) => key !== 'kind' && key !== 'status' && hasMeaningfulCreation(item))
  }
  return false
}
export function mergeCreationPreservingDraft<T extends Record<string, unknown>>(current: T, incoming: Record<string, unknown>): T {
  const next = { ...current }
  for (const [key, value] of Object.entries(incoming)) {
    if (key === 'kind' || key === 'status') continue
    if (value === undefined || value === null) continue
    if (typeof value === 'string' && !value.trim()) continue
    if (Array.isArray(value) && !value.length) continue
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      next[key as keyof T] = { ...((next[key as keyof T] as Record<string, unknown> | undefined) || {}), ...(value as Record<string, unknown>) } as T[keyof T]
    } else next[key as keyof T] = value as T[keyof T]
  }
  return next
}
export const kindLabel = (kind: Kind) => kind === 'topic' ? '选题' : kind === 'outline' ? '视频大纲' : '完整脚本'
export const materialIcon = (kind: Kind) => kind === 'topic' ? 'ri-lightbulb-line' : kind === 'outline' ? 'ri-list-check-2' : 'ri-file-text-line'
export const latestCreation = (project: ProjectDetail, kind: 'outline' | 'script'): StoredCreation | undefined => kind === 'outline' ? project.outlines?.slice(-1)[0] : project.scripts?.slice(-1)[0]
export const materialKey = (ref: Pick<MaterialRef, 'kind' | 'id'>) => `${ref.kind}:${ref.id}`
export const asTopicForm = (topic?: Topic): TopicForm => ({ title: topic?.title || '', summary: topic?.summary || '', angle: topic?.angle || '', fit: topic?.fit || '' })

export function inspirationText(item: InspirationItem) {
  return item.summary || item.content || '暂无详情'
}

export function topicPreview(topic: Topic): InspirationItem & Pick<Topic, 'angle' | 'fit'> {
  return { id: topic.id, kind: 'topic', title: topic.title, summary: topic.summary, angle: topic.angle, fit: topic.fit }
}

export function creationPreview(kind: 'outline' | 'script', item: StoredCreation): InspirationItem {
  const content = item.content
  const outline = kind === 'outline' ? content as Outline : undefined
  return {
    id: item.id || `${kind}-latest`,
    kind,
    title: content.title || kindLabel(kind),
    summary: kind === 'outline' ? outline?.outline_text || outline?.hook : (content as Script).script,
    content: kind === 'outline' ? outline?.core_content : (content as Script).script,
    hook: outline?.hook,
    tags: outline?.tags,
    outline_text: outline?.outline_text,
    core_content: outline?.core_content,
    project_id: item.project_id,
    created_at: item.created_at,
  }
}
